import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Mail, LogOut, Loader2, AlertTriangle, CheckCircle2, ArrowLeft,
  Download, Phone, Copy, FileText, Receipt, Link2, Building2,
} from "lucide-react";
import { clientClient, clientAuth, STATUS_LABEL, STATUS_COLOR } from "@/lib/api";
import OrderTimeline from "@/components/OrderTimeline";

const PHONE = "+420 775 659 129";

// -------- REQUEST MAGIC LINK --------
export function ClientLogin() {
  useEffect(() => { document.title = "Klientská zóna · STK Komfort"; }, []);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [debugUrl, setDebugUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await clientClient().post("/client/magic-link", { email });
      setSent(true);
      // If email integration is OFF, backend returns debug URL — show locally so flow can be tested
      if (data.magic_url_debug) setDebugUrl(data.magic_url_debug);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Něco se nepovedlo, zkuste to znovu.");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto pt-20">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-300">
          <ArrowLeft className="h-4 w-4" /> Zpět na úvod
        </Link>
        <div className="mt-6 rounded-3xl border border-amber-300/20 bg-white/[0.03] p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/30 text-amber-300">
            <Mail className="h-5 w-5" />
          </div>
          <h1 className="font-heading mt-5 text-2xl font-bold text-white">Klientská zóna</h1>
          <p className="mt-2 text-sm text-slate-400">Zadejte e-mail, který jste použili při objednávce. Pošleme vám bezpečný odkaz pro přihlášení — bez hesla.</p>

          {sent ? (
            <div className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm text-emerald-200" data-testid="client-magic-sent">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Odkaz vám pošleme na e-mail.</div>
                  <div className="mt-1 text-xs opacity-90">Platí 7 dní. Pokud zprávu neuvidíte, zkontrolujte i složku spam.</div>
                </div>
              </div>
              {debugUrl && (
                <div className="mt-4 border-t border-emerald-400/20 pt-3 text-xs">
                  <div className="font-semibold text-amber-200">Debug (e-mail integrace zatím vypnuta):</div>
                  <a href={debugUrl} className="block mt-1 break-all text-amber-300 underline" data-testid="client-magic-debug-url">{debugUrl}</a>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                type="email" placeholder="vas@email.cz"
                value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required
                data-testid="client-email"
                className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none"
              />
              {err && <div className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-xs text-rose-200 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{err}</div>}
              <button type="submit" disabled={busy} data-testid="client-send-magic"
                className="w-full rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 disabled:opacity-50 px-5 py-3 font-bold text-slate-900">
                {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Poslat přihlašovací odkaz"}
              </button>
              <p className="text-xs text-slate-500">
                Nemáte u nás zatím objednávku?{" "}
                <Link to="/objednavka" className="text-amber-300 hover:underline">Vytvořit objednávku</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </Shell>
  );
}

// -------- VERIFY MAGIC TOKEN --------
export function ClientVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Ověřujeme přihlášení · STK Komfort";
    if (!token) { setErr("Chybí přihlašovací odkaz."); return; }
    clientAuth.set(token);
    // Verify it actually works
    clientClient().get("/client/me")
      .then(() => nav("/klient/objednavky", { replace: true }))
      .catch((ex) => {
        clientAuth.clear();
        setErr(ex?.response?.data?.detail || "Odkaz vypršel nebo není platný. Vyžádejte si prosím nový.");
      });
  }, [token, nav]);

  return (
    <Shell>
      <div className="max-w-md mx-auto pt-32 text-center">
        {!err ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin inline text-amber-300" />
            <div className="mt-3 text-slate-300">Ověřujeme váš přihlašovací odkaz…</div>
          </>
        ) : (
          <div className="rounded-2xl border border-rose-400/30 bg-rose-400/10 p-6 text-rose-200" data-testid="client-verify-error">
            <AlertTriangle className="h-5 w-5 inline mr-2" /> {err}
            <div className="mt-4">
              <Link to="/klient" className="text-amber-300 hover:underline">Vyžádat nový odkaz</Link>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

// -------- DASHBOARD --------
export function ClientDashboard() {
  useEffect(() => { document.title = "Moje objednávky · STK Komfort"; }, []);
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, list] = await Promise.all([
        clientClient().get("/client/me"),
        clientClient().get("/client/orders"),
      ]);
      setEmail(me.data.email);
      setOrders(list.data);
      if (list.data.length > 0) setExpanded(list.data[0].order_number);
    } catch (ex) {
      if (ex?.response?.status === 401) { clientAuth.clear(); nav("/klient"); }
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const logout = () => { clientAuth.clear(); nav("/klient"); };

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <Link to="/" className="flex items-center gap-3">
          <span className="block h-10 w-10 rounded-xl bg-[#0A0D17] ring-1 ring-amber-300/20"
            style={{ backgroundImage: "url(/brand/logo.png)", backgroundRepeat: "no-repeat", backgroundSize: "150% auto", backgroundPosition: "50% 18%" }} />
          <div>
            <div className="font-heading text-base font-bold text-white">STK Komfort · Klientská zóna</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">{email}</div>
          </div>
        </Link>
        <button onClick={logout} data-testid="client-logout"
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-2 text-xs text-white">
          <LogOut className="h-4 w-4" /> Odhlásit
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Načítám…</div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-slate-500">
          Zatím nemáte žádné objednávky. <Link to="/objednavka" className="text-amber-300 hover:underline">Vytvořit objednávku</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="client-orders">
          {orders.map((o) => (
            <OrderCard key={o.order_number} order={o} expanded={expanded === o.order_number} onToggle={() => setExpanded(expanded === o.order_number ? null : o.order_number)} onReload={load} />
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200">
          <Phone className="h-4 w-4" /> Potřebujete pomoc? Zavolejte Lence: {PHONE}
        </a>
      </div>
    </Shell>
  );
}

function OrderCard({ order, expanded, onToggle }) {
  const [copied, setCopied] = useState("");
  const colorMap = {
    awaiting_payment: "border-amber-300/40 bg-amber-300/5",
    paid: "border-emerald-400/40 bg-emerald-400/5",
    invoiced: "border-sky-400/40 bg-sky-400/5",
    done: "border-emerald-400/40 bg-emerald-400/5",
    cancelled: "border-rose-400/40 bg-rose-400/5",
  };
  const color = colorMap[order.status] || "border-white/10 bg-white/[0.03]";
  const copy = (text, key) => { navigator.clipboard?.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); };

  const openPdf = async (kind) => {
    try {
      const res = await clientClient().get(`/client/orders/${order.order_number}/${kind}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind === "invoice" ? order.invoice_number : order.proforma_number || order.order_number}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (ex) { console.error(ex); }
  };

  return (
    <div className={`rounded-3xl border ${color} p-5 lg:p-6 transition-all`} data-testid={`client-order-${order.order_number}`}>
      <button onClick={onToggle} className="w-full text-left">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading text-xl font-bold text-amber-200">{order.order_number}</span>
              <StatusBadge status={order.status} />
            </div>
            <div className="mt-1 text-sm text-slate-200">{order.service_name}</div>
            <div className="mt-1 text-xs text-slate-500">{new Date(order.created_at).toLocaleString("cs-CZ")}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-extrabold text-amber-200">{order.amount.toFixed(2)} Kč</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">VS {order.variable_symbol}</div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="mt-6 grid lg:grid-cols-12 gap-6 border-t border-white/10 pt-6">
          {/* Timeline */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.18em] text-amber-300/80 font-semibold mb-3">Sledování objednávky</div>
            <OrderTimeline order={order} />
          </div>

          {/* QR + payment + PDFs */}
          <div className="lg:col-span-7 space-y-4">
            {(order.status === "awaiting_payment") && (
              <div className="rounded-2xl border border-amber-300/30 bg-white p-5 grid sm:grid-cols-5 gap-5 items-center">
                <div className="sm:col-span-2 flex justify-center">
                  <QRCodeSVG value={order.spayd} size={150} level="M" includeMargin />
                </div>
                <div className="sm:col-span-3 text-slate-900">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold flex items-center gap-1"><Building2 className="h-3 w-3" />QR Platba</div>
                  <div className="mt-1 font-heading text-2xl font-extrabold">{order.amount.toFixed(2)} Kč</div>
                  <CopyRow label="IBAN" value={order.iban_pretty} k={`${order.order_number}-iban`} copied={copied} onCopy={copy} light />
                  <CopyRow label="VS" value={order.variable_symbol} k={`${order.order_number}-vs`} copied={copied} onCopy={copy} light highlight />
                  <CopyRow label="Účet" value={order.account_number} k={`${order.order_number}-acc`} copied={copied} onCopy={copy} light />
                </div>
              </div>
            )}

            {order.status === "paid" && (
              <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-emerald-200 text-sm flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                <div>Platba je u nás. Náš tým připravuje fakturu a domluví se s vámi na realizaci služby.</div>
              </div>
            )}

            {order.invoice_number && (
              <div className="rounded-2xl border border-sky-400/30 bg-sky-400/5 p-4 text-sky-200 text-sm flex items-start gap-2">
                <Receipt className="h-5 w-5 shrink-0 mt-0.5" />
                <div>Faktura <strong className="text-white">{order.invoice_number}</strong> byla vystavena a je dostupná ke stažení.</div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button onClick={() => openPdf("proforma")} data-testid={`client-pdf-proforma-${order.order_number}`}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300/30 bg-amber-300/10 hover:bg-amber-300/20 px-4 py-2.5 text-sm font-semibold text-amber-200">
                <Download className="h-4 w-4" /> Platební výzva PDF
              </button>
              {order.invoice_number && (
                <button onClick={() => openPdf("invoice")} data-testid={`client-pdf-invoice-${order.order_number}`}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900">
                  <Download className="h-4 w-4" /> Stáhnout fakturu
                </button>
              )}
              <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-4 py-2.5 text-sm font-semibold text-white">
                <Phone className="h-4 w-4" /> Zavolat Lence
              </a>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Detail objednávky</div>
              <dl className="mt-2 space-y-1 text-xs">
                {order.vehicle && <FRow k="Vozidlo" v={order.vehicle} />}
                {order.spz_vin && <FRow k="SPZ / VIN" v={order.spz_vin} />}
                {order.preferred_term && <FRow k="Preferovaný termín" v={order.preferred_term} />}
                {order.proforma_number && <FRow k="Platební výzva č." v={order.proforma_number} />}
                {order.invoice_number && <FRow k="Faktura č." v={order.invoice_number} />}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- shared --------
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0D17] text-slate-200">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">{children}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || "slate";
  const colorMap = {
    amber: "bg-amber-300/15 border-amber-300/40 text-amber-200",
    emerald: "bg-emerald-400/15 border-emerald-400/40 text-emerald-200",
    blue: "bg-sky-400/15 border-sky-400/40 text-sky-200",
    rose: "bg-rose-400/15 border-rose-400/40 text-rose-200",
    slate: "bg-white/[0.04] border-white/10 text-slate-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${colorMap[color]}`}>{STATUS_LABEL[status] || status}</span>;
}
function CopyRow({ label, value, k, copied, onCopy, light, highlight }) {
  return (
    <div className="mt-2 flex items-center justify-between gap-3">
      <div>
        <div className={`text-[10px] uppercase tracking-[0.15em] ${light ? "text-slate-500" : "text-slate-500"}`}>{label}</div>
        <div className={`font-mono text-sm ${highlight ? "font-bold text-amber-700" : light ? "text-slate-900" : "text-white"}`}>{value}</div>
      </div>
      <button type="button" onClick={() => onCopy(value, k)} className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${light ? "border-slate-200 bg-slate-50 text-slate-500 hover:text-amber-700" : "border-white/10 bg-white/5 text-slate-300"}`} aria-label="Kopírovat">
        {copied === k ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
function FRow({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-white text-right">{v}</dd>
    </div>
  );
}

// Avoid unused-import errors (Link2, FileText are referenced indirectly via UI strings; suppress lint warning)
void Link2; void FileText;
